// The marriage intents (diplomacy.dynasty), kept out of useSimIntents to
// stay under the 400-line limit. Each answers whether the sim took it.

import type { createRustSimAdapter } from '@aod/sim-adapter';
import { type RefObject, useMemo } from 'react';

export interface DynastyActions {
  /** Offer a marriage to an allied seat. */
  readonly propose: (toPlayer: number) => boolean;
  /** Accept the marriage offered by `fromPlayer`. */
  readonly accept: (fromPlayer: number) => boolean;
  /** Refuse it. */
  readonly decline: (fromPlayer: number) => boolean;
}

/** What a sim without the module (or before it boots) offers: nothing happens. */
export const NO_DYNASTY: DynastyActions = {
  propose: () => false,
  accept: () => false,
  decline: () => false,
};

export function useDynastyIntents(
  adapterRef: RefObject<ReturnType<typeof createRustSimAdapter> | null>,
  localPlayerIndex: number,
): DynastyActions {
  return useMemo(() => {
    const send = (kind: string, field: string, seat: number): boolean => {
      const adapter = adapterRef.current;
      if (!adapter) return false;
      return adapter.intent({ kind, playerIndex: localPlayerIndex, [field]: seat } as never).ok;
    };
    return {
      propose: (to) => send('propose_marriage', 'toPlayer', to),
      accept: (from) => send('accept_marriage', 'fromPlayer', from),
      decline: (from) => send('decline_marriage', 'fromPlayer', from),
    };
  }, [adapterRef, localPlayerIndex]);
}
